import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import StyledModal from '../../../../components/modal/styledModals/StyledModal';
import SvgFragment from '../../../../fragments/SvgFragment';
import { styles } from '../../../../styles/themes/style';
import {
  IUnipileConnectForm,
  DEFAULT_UNIPILE_CONNECT_FORM,
  PROXY_COUNTRY_OPTIONS,
  LinkedInConnectMethod,
} from '../../../../interfaces/models/campaign';
import { checkpointHint, checkpointMode } from '../../utils/linkedInCheckpoint';

type FieldErrors = Partial<Record<'country' | 'liEmail' | 'liPassword' | 'liAt' | 'liA', string>>;

interface Props {
  show: boolean;
  /** 'reconnect' re-authenticates an existing account instead of adding a new one. */
  mode?: 'add' | 'reconnect';
  /** Display name of the account being reconnected — shown in the description. */
  accountName?: string | null;
  submitting?: boolean;
  onHide: () => void;
  onSubmit: (form: IUnipileConnectForm) => void;

  /**
   * Why the last attempt failed, rendered inside the dialog.
   *
   * Optional because the campaign form reports failures on a toast. Settings has no
   * toast here, and a page-level error bar renders BEHIND this modal — so a refused
   * login looked to the user like the button had done nothing at all.
   */
  error?: string | null;
  /** Offered beside a seat-limit error, so the fix sits next to the problem. */
  onBuySeat?: () => void;
  /** Overrides the description — the campaign's wording is about a campaign. */
  description?: React.ReactNode;

  /**
   * Set to move to the verification step. LinkedIn asks most accounts for a code, and
   * handling it as a second step of the same dialog keeps one task in one place — it
   * used to appear as a separate panel elsewhere on the page.
   */
  checkpointType?: string | null;
  /** Solves the checkpoint. `code` is omitted for in-app approval, which has none. */
  onVerify?: (code?: string) => void;
  /**
   * Returns to the credentials step, discarding the pending checkpoint.
   *
   * Without it Back falls through to onHide, which closes the dialog outright —
   * fine as a last resort, wrong as the meaning of a button labelled Back.
   */
  onBack?: () => void;
}

/**
 * Validation is per-field so the form can point at what is actually wrong instead
 * of collapsing everything into a single toast. Connection outcomes (success,
 * wrong credentials, checkpoint) stay on toasts — they come from the server.
 */
function validate(form: IUnipileConnectForm): FieldErrors {
  const errors: FieldErrors = {};

  if (!/^[A-Z]{2}$/.test(form.country.trim().toUpperCase())) {
    errors.country = 'Pick the country the account should connect from.';
  }

  if (form.method === 'cookies') {
    if (!form.liAt.trim()) errors.liAt = 'Paste the li_at cookie from this browser.';
    return errors;
  }

  const email = form.liEmail.trim();
  if (!email) errors.liEmail = 'Enter the email you use to sign in to LinkedIn.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.liEmail = 'Enter a valid email address.';

  if (!form.liPassword) errors.liPassword = 'Enter your LinkedIn password.';

  return errors;
}

const AddLinkedInAccountModal: React.FC<Props> = ({
  show, mode = 'add', accountName, submitting = false, onHide, onSubmit,
  error, onBuySeat, description, checkpointType, onVerify, onBack,
}) => {
  const [code, setCode] = useState('');
  /** Step 2 exists only once the caller reports a checkpoint — it cannot be skipped to. */
  const verifying = !!checkpointType && !!onVerify;
  const verifyMode = checkpointMode(checkpointType || '');
  const [form, setForm] = useState<IUnipileConnectForm>({ ...DEFAULT_UNIPILE_CONNECT_FORM });
  const [errors, setErrors] = useState<FieldErrors>({});
  // Errors appear on the first submit, then track every keystroke.
  const [submitted, setSubmitted] = useState(false);

  const isReconnect = mode === 'reconnect';

  useEffect(() => {
    if (!show) return;
    setForm({ ...DEFAULT_UNIPILE_CONNECT_FORM });
    setErrors({});
    setSubmitted(false);
    setCode('');
  }, [show]);

  const update = (patch: Partial<IUnipileConnectForm>) => {
    const next = { ...form, ...patch };
    setForm(next);
    if (submitted) setErrors(validate(next));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const found = validate(form);
    setSubmitted(true);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    onSubmit({
      ...form,
      liEmail: form.liEmail.trim(),
      liAt: form.liAt.trim(),
      liA: form.liA.trim(),
      country: form.country.trim().toUpperCase(),
    });
  };

  /*
    Step 2. A separate branch rather than fields appended to step 1: the credentials
    are already accepted by the time this shows, and leaving them on screen invited
    people to edit them and resubmit, which starts a second login and invalidates the
    code they were just sent.
  */
  if (verifying) {
    return (
      <Dialog show={show} onHide={submitting ? undefined : onHide} width="480px" backdrop="static">
        <Body as="div">
          <Header>
            <Title>Verify LinkedIn account</Title>
            <CloseBtn type="button" onClick={onHide} disabled={submitting} aria-label="Close">
              <SvgFragment type="close" />
            </CloseBtn>
          </Header>

          {/* IN_APP_VALIDATION has no code — there is nothing to type, you approve on
              your phone. Promising a code and then showing no field to put one in is
              how someone ends up waiting for an email that is never coming. */}
          <Description>
            {verifyMode === 'approve'
              ? 'LinkedIn needs you to approve this sign-in before the account can be connected.'
              : 'LinkedIn requires a verification code before this account can be connected.'}
          </Description>

          <AccountChip>
            <ChipMark>in</ChipMark>
            <div>
              <ChipName>{form.liEmail.trim() || accountName || 'LinkedIn account'}</ChipName>
              <ChipSub>LinkedIn account</ChipSub>
            </div>
          </AccountChip>

          <InfoNote>
            <InfoMark aria-hidden />
            <span>
              {checkpointHint(checkpointType || '')}
              {/* Whoever is connecting the account is often not the person LinkedIn
                  sent the code to — an admin wiring up a teammate's account cannot
                  read their authenticator. */}
              {verifyMode === 'code' && ' If this is not your account, ask the owner for the code.'}
            </span>
          </InfoNote>

          {verifyMode === 'code' && (
            <Field>
              <FieldLabel htmlFor="li-code">Verification code</FieldLabel>
              <Input
                id="li-code"
                autoFocus
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="Enter 6-digit code"
                value={code}
                disabled={submitting}
                onChange={e => setCode(e.target.value)}
              />
            </Field>
          )}

          {!!error && <SubmitError role="alert">{error}</SubmitError>}

          <Footer>
            {/* Back goes back — to the credentials step. It used to call onHide, which
                closed the whole dialog: a button labelled Back that cancelled. */}
            <SecondaryBtn
              type="button"
              onClick={() => { setCode(''); onBack ? onBack() : onHide(); }}
              disabled={submitting}
            >
              Back
            </SecondaryBtn>
            <PrimaryBtn
              type="button"
              disabled={submitting || (verifyMode === 'code' && !code.trim())}
              onClick={() => onVerify?.(verifyMode === 'code' ? code.trim() : undefined)}
            >
              {submitting
                ? 'Verifying…'
                : verifyMode === 'approve'
                  ? "I've approved — Continue"
                  : 'Connect account'}
            </PrimaryBtn>
          </Footer>
        </Body>
      </Dialog>
    );
  }

  const setMethod = (method: LinkedInConnectMethod) => {
    update({ method });
  };

  // Chrome skips read-only fields, so a saved login is not written in on open.
  // Focus removes it and paste still works.
  const unlockCookieField = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.readOnly = false;
  };

  const usingCookies = form.method === 'cookies';

  return (
    <Dialog show={show} onHide={submitting ? undefined : onHide} width="480px" backdrop="static">
      <Body onSubmit={handleSubmit} noValidate>
        <Header>
          <Title>{isReconnect ? 'Reconnect LinkedIn account' : 'Add new LinkedIn account'}</Title>
          <CloseBtn type="button" onClick={onHide} disabled={submitting} aria-label="Close">
            <SvgFragment type="close" />
          </CloseBtn>
        </Header>

        <Description>
          {description ?? 'Choose how you want to connect your LinkedIn account'}
        </Description>

        <MethodRow role="radiogroup" aria-label="LinkedIn sign-in method">
          <MethodCard
            type="button"
            role="radio"
            aria-checked={!usingCookies}
            $active={!usingCookies}
            disabled={submitting}
            onClick={() => setMethod('credentials')}
          >
            <Radio $active={!usingCookies} aria-hidden />
            <MethodCopy>
              <MethodTitle>Login credentials</MethodTitle>
              <MethodText>Use your LinkedIn email and password.</MethodText>
            </MethodCopy>
          </MethodCard>
          <MethodCard
            type="button"
            role="radio"
            aria-checked={usingCookies}
            $active={usingCookies}
            disabled={submitting}
            onClick={() => setMethod('cookies')}
          >
            <Radio $active={usingCookies} aria-hidden />
            <MethodCopy>
              <MethodTitle>Session cookies</MethodTitle>
              <MethodText>Use your existing LinkedIn browser session.</MethodText>
            </MethodCopy>
          </MethodCard>
        </MethodRow>

        {usingCookies && (
          <InfoNote>
            <InfoMark aria-hidden />
            <span>
              Uses the LinkedIn session already open in this browser. Works with LinkedIn, Sales Navigator, and Recruiter. If you log out in this browser, this account will disconnect.
            </span>
          </InfoNote>
        )}

        <Fields>
          {usingCookies ? (
            <>
              <Field>
                <FieldLabel htmlFor="li-at">LinkedIn session cookie</FieldLabel>
                <Input
                  id="li-at"
                  spellCheck={false}
                  autoComplete="off"
                  readOnly
                  onFocus={unlockCookieField}
                  placeholder="Paste your li_at cookie"
                  $invalid={!!errors.liAt}
                  value={form.liAt}
                  disabled={submitting}
                  onChange={e => update({ liAt: e.target.value })}
                />
                {errors.liAt
                  ? <FieldError>{errors.liAt}</FieldError>
                  : (
                    <FieldHint>
                      Use the li_at cookie from your active LinkedIn session.{' '}
                      <HintLink
                        href="https://developer.chrome.com/docs/devtools/application/cookies"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        How to find it
                      </HintLink>
                    </FieldHint>
                  )}
              </Field>

              <Field>
                <FieldLabel htmlFor="li-a">Sales Navigator / Recruiter cookie (optional)</FieldLabel>
                <Input
                  id="li-a"
                  spellCheck={false}
                  autoComplete="off"
                  readOnly
                  onFocus={unlockCookieField}
                  placeholder="Paste your li_a cookie"
                  $invalid={!!errors.liA}
                  value={form.liA}
                  disabled={submitting}
                  onChange={e => update({ liA: e.target.value })}
                />
                {errors.liA
                  ? <FieldError>{errors.liA}</FieldError>
                  : <FieldHint>Only needed if you use Sales Navigator or Recruiter.</FieldHint>}
              </Field>
            </>
          ) : (
            <>
              <Field>
                <FieldLabel htmlFor="li-email">LinkedIn email</FieldLabel>
                <Input
                  id="li-email"
                  type="email"
                  autoComplete="off"
                  placeholder="you@example.com"
                  $invalid={!!errors.liEmail}
                  value={form.liEmail}
                  disabled={submitting}
                  onChange={e => update({ liEmail: e.target.value })}
                />
                {errors.liEmail && <FieldError>{errors.liEmail}</FieldError>}
              </Field>

              <Field>
                <FieldLabel htmlFor="li-password">LinkedIn password</FieldLabel>
                <Input
                  id="li-password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="••••••••"
                  $invalid={!!errors.liPassword}
                  value={form.liPassword}
                  disabled={submitting}
                  onChange={e => update({ liPassword: e.target.value })}
                />
                {errors.liPassword && <FieldError>{errors.liPassword}</FieldError>}
              </Field>
            </>
          )}

          <Field>
            <FieldLabel htmlFor="li-country">Country</FieldLabel>
            <Select
              id="li-country"
              $invalid={!!errors.country}
              value={form.country}
              disabled={submitting}
              onChange={e => update({ country: e.target.value })}
            >
              {PROXY_COUNTRY_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Select>
            {errors.country && <FieldError>{errors.country}</FieldError>}
          </Field>
        </Fields>

        {/* Inside the dialog, above the buttons. A seat limit is the one failure with
            an obvious next step, so it carries the link rather than only the news. */}
        {!!error && (
          <SubmitError role="alert">
            {error}
            {onBuySeat && /seat|connected LinkedIn account/i.test(error) && (
              <SeatLink type="button" onClick={onBuySeat}>Buy a seat</SeatLink>
            )}
          </SubmitError>
        )}

        <Footer>
          <SecondaryBtn type="button" onClick={onHide} disabled={submitting}>Cancel</SecondaryBtn>
          <PrimaryBtn type="submit" disabled={submitting}>
            {submitting
              ? (isReconnect ? 'Reconnecting…' : 'Connecting…')
              : (usingCookies ? 'Connect account' : 'Continue')}
          </PrimaryBtn>
        </Footer>
      </Body>
    </Dialog>
  );
};

const Dialog = styled(StyledModal)`
  .modal-content {
    border: none;
    border-radius: 4px;
    box-shadow: 0px 0px 12px rgba(51, 57, 81, 0.1);
  }
`;

export default AddLinkedInAccountModal;

const Body = styled.form`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  padding: 32px 24px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 28px;
  width: 100%;
  position: relative;
`;

const Title = styled.h3`
  margin: 0;
  flex: 1;
  font-family: NunitoSans;
  font-size: 24px;
  font-weight: 700;
  line-height: 28px;
  color: #333951;
`;

const CloseBtn = styled.button`
  background: none; border: none; padding: 0; cursor: pointer; line-height: 0;
  width: 24px; height: 24px; flex-shrink: 0;
  color: #8C96AD;
  .svg { width: 24px; height: 24px; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const Description = styled.p`
  margin: 0;
  width: 100%;
  font-family: NunitoSans;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #333951;
  strong { font-weight: 700; }
`;

const SubmitError = styled.div`
  display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap;
  width: 100%;
  border: 1px solid #fecaca; background: #fef2f2; border-radius: 8px;
  padding: 9px 11px;
  font-family: NunitoSans; font-size: 12px; line-height: 1.5; color: #b91c1c;
`;

const SeatLink = styled.button`
  border: none; background: none; padding: 0; cursor: pointer;
  font-family: NunitoSans; font-size: 12px; font-weight: 800; color: #b91c1c;
  text-decoration: underline;
`;

const InfoMark = styled.span`
  position: relative;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  margin-top: 2px;
  border: 1.5px solid #5964FF;
  border-radius: 50%;
  &::before, &::after {
    content: '';
    position: absolute;
    left: 50%;
    width: 1.5px;
    background: #5964FF;
    transform: translateX(-50%);
    border-radius: 1px;
  }
  &::before { top: 3px; height: 1.5px; }
  &::after { top: 6px; height: 4px; }
`;

const InfoNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  box-sizing: border-box;
  width: 100%;
  padding: 8px 12px;
  background: #EEF3FF;
  border: 1px solid #5964FF;
  border-radius: 10px;
  font-family: NunitoSans;
  font-size: 12px;
  font-weight: 400;
  line-height: 19px;
  color: #5964FF;
`;

/* The account being verified, so the code is never typed against the wrong one. */
const AccountChip = styled.div`
  display: flex; align-items: center; gap: 10px;
  border: 1px solid #e2e8f0; border-radius: 8px; padding: 9px 11px;
`;

const ChipMark = styled.span`
  display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
  width: 26px; height: 26px; border-radius: 6px;
  background: #0a66c2; color: #fff;
  font-family: NunitoSans; font-size: 12px; font-weight: 800;
`;

const ChipName = styled.div`
  font-family: NunitoSans; font-size: 13px; font-weight: 700; color: ${styles.colors.black600};
`;

const ChipSub = styled.div`
  font-family: NunitoSans; font-size: 11px; color: ${styles.colors.black400};
`;
const MethodRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`;

const MethodCard = styled.button<{ $active?: boolean }>`
  flex: 1;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  box-sizing: border-box;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid ${({ $active }) => ($active ? '#5964FF' : '#DAE0E8')};
  background: #fff;
  text-align: left;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: default; }
`;

const Radio = styled.span<{ $active?: boolean }>`
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  margin-top: 4px;
  box-sizing: border-box;
  border-radius: 50%;
  border: 1.5px solid ${({ $active }) => ($active ? '#5964FF' : '#DAE0E8')};
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #6061F8;
    display: ${({ $active }) => ($active ? 'block' : 'none')};
  }
`;

const MethodCopy = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
`;

const MethodTitle = styled.span`
  font-family: NunitoSans;
  font-size: 13px;
  font-weight: 800;
  line-height: 21px;
  color: #333951;
`;

const MethodText = styled.span`
  padding-top: 4px;
  font-family: NunitoSans;
  font-size: 12px;
  font-weight: 400;
  line-height: 17px;
  color: #545E77;
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
`;

const FieldLabel = styled.label`
  font-family: NunitoSans;
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
  color: #545E77;
`;

const controlStyles = `
  height: 40px;
  border-radius: 10px;
  font-family: NunitoSans;
  font-size: 12px;
  line-height: 16px;
  color: #333951;
  background: #fff;
  outline: none;
  width: 100%;
  box-sizing: border-box;
`;

const Input = styled.input<{ $invalid?: boolean }>`
  ${controlStyles}
  padding: 0 12px;
  border: 1px solid ${({ $invalid }) => ($invalid ? '#ef4444' : '#E8EAED')};
  &::placeholder { color: #8C96AD; font-weight: 400; }
  &:focus { border-color: ${({ $invalid }) => ($invalid ? '#ef4444' : '#5964FF')}; }
  &:disabled { background: #f7f9fc; cursor: default; }
`;

const Select = styled.select<{ $invalid?: boolean }>`
  ${controlStyles}
  padding: 0 28px 0 12px;
  border: 1px solid ${({ $invalid }) => ($invalid ? '#ef4444' : '#E8EAED')};
  appearance: none;
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%231C212C' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  &:focus { border-color: ${({ $invalid }) => ($invalid ? '#ef4444' : '#5964FF')}; }
  &:disabled { background: #f7f9fc; cursor: default; }
`;

const FieldHint = styled.span`
  font-family: NunitoSans;
  font-size: 11px;
  font-weight: 400;
  line-height: 16px;
  color: #768097;
`;

const HintLink = styled.a`
  color: #5964FF;
  font-weight: 700;
  text-decoration: underline;
`;

const FieldError = styled.span`
  font-family: NunitoSans;
  font-size: 11px;
  font-weight: 400;
  line-height: 16px;
  color: #dc2626;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  width: 100%;
  margin-top: 8px;
`;

const SecondaryBtn = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-family: NunitoSans;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: #8C96AD;
  &:disabled { opacity: 0.5; cursor: default; }
`;

const PrimaryBtn = styled.button`
  height: 34px;
  padding: 7px 8px;
  border: none;
  border-radius: 4px;
  background: #5964FF;
  color: #fff;
  font-family: NunitoSans;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  cursor: pointer;
  &:hover:not(:disabled) { background: ${styles.colors.primary500}; }
  &:disabled { background: ${styles.colors.black200}; cursor: default; }
`;
